import {
  ErrorTypes,
  type ValidationError,
} from '../submission-validation/errors';
import type {
  Validation,
  DeriveErrorOptions,
  DateFieldValidationError,
  DateTimeFieldValidationError,
} from './types';
import { DateFieldErrorSuffix, DateTimeFieldErrorSuffix } from './types';
import {
  getDecimalPlacesCount,
  deserializeDate,
  parseDateTimeValue,
  isTimeComplete,
} from './helpers';

type DeriveErrorReturn =
  | ValidationError
  | DateFieldValidationError
  | DateTimeFieldValidationError
  | undefined;

export function deriveError(
  validation?: Validation,
  error?: ValidationError,
  options?: DeriveErrorOptions,
): DeriveErrorReturn {
  if (!error) {
    return error;
  }

  return (
    derivePatternError(error) ??
    deriveCharacterLengthError(validation, error) ??
    deriveItemsCountError(validation, error) ??
    deriveNumericValueError(validation, error) ??
    deriveDateFieldError(validation, error, options?.value) ??
    deriveDateTimeFieldError(validation, error, options?.value) ??
    error
  );
}

export function derivePatternError(
  error?: ValidationError,
): ValidationError | undefined {
  if (!error || error.errorType !== ErrorTypes.PATTERN_ERROR) {
    return undefined;
  }

  return {
    ...error,
    errorType: ErrorTypes.INVALID_VALUE_FOR_PATTERN_ERROR,
  };
}

export function deriveCharacterLengthError(
  validation?: Validation,
  error?: ValidationError,
): ValidationError | undefined {
  if (!error) {
    return undefined;
  }

  const isMinMaxLengthError =
    error.errorType === ErrorTypes.MIN_LENGTH_ERROR ||
    error.errorType === ErrorTypes.MAX_LENGTH_ERROR;

  if (!isMinMaxLengthError) {
    return undefined;
  }

  const minLength = validation?.string?.minLength;
  const maxLength = validation?.string?.maxLength;
  const hasMinMax = minLength && maxLength;

  if (hasMinMax && minLength === maxLength) {
    return {
      ...error,
      errorType: ErrorTypes.EXACT_CHARACTER_LENGTH_ERROR,
      errorMessage: `must have exactly ${minLength} characters`,
    };
  }

  if (hasMinMax && minLength !== maxLength) {
    return {
      ...error,
      errorType: ErrorTypes.CHARACTER_LENGTH_RANGE_ERROR,
      errorMessage: `must have between ${minLength} and ${maxLength} characters`,
      params: {
        minLimit: minLength,
        maxLimit: maxLength,
      },
    };
  }

  return undefined;
}

export function deriveItemsCountError(
  validation?: Validation,
  error?: ValidationError,
): ValidationError | undefined {
  if (!error) {
    return undefined;
  }

  const isMinMaxItemsError =
    error.errorType === ErrorTypes.MIN_ITEMS_ERROR ||
    error.errorType === ErrorTypes.MAX_ITEMS_ERROR;

  if (!isMinMaxItemsError) {
    return undefined;
  }

  const minItems = validation?.array?.minItems;
  const maxItems = validation?.array?.maxItems;
  const hasMinMax = minItems && maxItems;

  if (hasMinMax && minItems === maxItems) {
    return {
      ...error,
      errorType: ErrorTypes.EXACT_ITEMS_NUMBER_ERROR,
      errorMessage: `must choose ${minItems} options`,
    };
  }

  return undefined;
}

export function deriveNumericValueError(
  validation?: Validation,
  error?: ValidationError,
): ValidationError | undefined {
  if (!error) {
    return undefined;
  }

  const isMinMaxValueError =
    error.errorType === ErrorTypes.MIN_VALUE_ERROR ||
    error.errorType === ErrorTypes.MAX_VALUE_ERROR;

  const isMultipleOfValueError =
    error.errorType === ErrorTypes.MULTIPLE_OF_VALUE_ERROR;

  if (isMinMaxValueError) {
    const { minPrice, maxPrice } =
      validation?.predefined?.paymentOptions?.products?.[0]
        ?.dynamicPriceOptions ?? {};

    const minimum = validation?.number?.minimum ?? minPrice;
    const maximum = validation?.number?.maximum ?? maxPrice;
    const hasMinMax = minimum !== undefined && maximum !== undefined;

    if (hasMinMax && minimum !== maximum) {
      return {
        ...error,
        errorType: ErrorTypes.VALUE_RANGE_ERROR,
        errorMessage: `must be from ${minimum} to ${maximum}`,
        params: {
          minLimit: minimum,
          maxLimit: maximum,
        },
      };
    }
  }

  if (isMultipleOfValueError) {
    const multipleOf = validation?.number?.multipleOf;

    if (multipleOf) {
      const decimalPlaces = getDecimalPlacesCount(multipleOf);

      return {
        ...error,
        errorType: ErrorTypes.DECIMAL_POINT_ERROR,
        errorMessage: `must have ${decimalPlaces} number(s) after the decimal point`,
        params: {
          number: decimalPlaces,
        },
      };
    }
  }

  return undefined;
}

export function deriveDateFieldError(
  _validation?: Validation,
  error?: ValidationError,
  value?: string,
): ValidationError | DateFieldValidationError | undefined {
  const isFormatError = error?.errorType === ErrorTypes.FORMAT_ERROR;

  if (!error || !isFormatError) {
    return undefined;
  }

  const { YEAR: _YEAR, MONTH, DAY } = deserializeDate(value);
  const YEAR = _YEAR?.length === 4;

  if (!YEAR && !MONTH && !DAY) {
    return undefined;
  }

  const withErrorSuffix = (
    suffix: DateFieldErrorSuffix,
  ): DateFieldValidationError => ({
    ...error,
    errorType: ErrorTypes.INCOMPLETE_DATE_ERROR,
    params: {
      suffix,
    },
  });

  if (!YEAR && !MONTH) {
    return withErrorSuffix(DateFieldErrorSuffix.YEAR_MONTH);
  } else if (!YEAR && !DAY) {
    return withErrorSuffix(DateFieldErrorSuffix.YEAR_DAY);
  } else if (!MONTH && !DAY) {
    return withErrorSuffix(DateFieldErrorSuffix.MONTH_DAY);
  } else if (!YEAR) {
    return withErrorSuffix(DateFieldErrorSuffix.YEAR);
  } else if (!MONTH) {
    return withErrorSuffix(DateFieldErrorSuffix.MONTH);
  } else if (!DAY) {
    return withErrorSuffix(DateFieldErrorSuffix.DAY);
  } else {
    return undefined;
  }
}

export function deriveDateTimeFieldError(
  validation?: Validation,
  error?: ValidationError,
  value?: string,
): ValidationError | DateTimeFieldValidationError | undefined {
  const isFormatError = error?.errorType === ErrorTypes.FORMAT_ERROR;

  if (!error || !isFormatError) {
    return undefined;
  }

  const [date, time] = parseDateTimeValue(value);
  const dateValidationError = deriveDateFieldError(validation, error, date);
  const { YEAR, MONTH, DAY } = deserializeDate(date);
  const timeComplete = isTimeComplete(time);

  if (!timeComplete && YEAR?.length !== 4 && !MONTH && !DAY) {
    return undefined;
  }

  if (!timeComplete) {
    const withErrorSuffix = (
      suffix: DateTimeFieldErrorSuffix,
    ): DateTimeFieldValidationError => ({
      ...error,
      errorType: ErrorTypes.INCOMPLETE_DATE_ERROR,
      params: {
        suffix,
      },
    });

    const isDateFieldError = (
      validationError?: ValidationError,
    ): validationError is DateFieldValidationError => {
      return validationError?.errorType === ErrorTypes.INCOMPLETE_DATE_ERROR;
    };

    if (isDateFieldError(dateValidationError)) {
      switch (dateValidationError.params.suffix) {
        case DateFieldErrorSuffix.YEAR_MONTH:
          return withErrorSuffix(DateTimeFieldErrorSuffix.YEAR_MONTH_TIME);
        case DateFieldErrorSuffix.YEAR_DAY:
          return withErrorSuffix(DateTimeFieldErrorSuffix.YEAR_DAY_TIME);
        case DateFieldErrorSuffix.MONTH_DAY:
          return withErrorSuffix(DateTimeFieldErrorSuffix.MONTH_DAY_TIME);
        case DateFieldErrorSuffix.YEAR:
          return withErrorSuffix(DateTimeFieldErrorSuffix.YEAR_TIME);
        case DateFieldErrorSuffix.MONTH:
          return withErrorSuffix(DateTimeFieldErrorSuffix.MONTH_TIME);
        case DateFieldErrorSuffix.DAY:
          return withErrorSuffix(DateTimeFieldErrorSuffix.DAY_TIME);
      }
    }

    return withErrorSuffix(DateTimeFieldErrorSuffix.TIME);
  }

  return dateValidationError;
}
